"use client";
import * as QueryString from "querystring";
import {
  API_ACCEPT,
  AuthApiPath,
  CONTENT_TYPE_FORM,
} from "@/lib/auth/constants";
import newrelic from "@/lib/monitoring/newrelic";
import { getAuthQueryParams } from "@/lib/auth/getAuthQueryParams";
import { requestAndBindCsrfToHeaders } from "@/lib/auth/requestAndBindCsrfToHeaders";
import { buildApiEndpoint } from "@/lib/auth/buildApiEndpoint";
import { QueryParams } from "@/utils/functions/buildSafeUrl";

export type LoginSuccessResponse = {
  data: { redirect_url: string; [key: string]: unknown };
  success: true;
};
export type LoginErrorResponse = {
  success: false;
  errorMessage: string;
  errorCode?: string;
  status: number;
  raw?: any;
};
export type LoginResponse = LoginSuccessResponse | LoginErrorResponse;

type LoginOptionsAndAnalytics = { enroll_course_id: string };

function analyticsAndOptionsForLogin(qps: QueryParams) {
  if (qps.course_id) {
    return { enroll_course_id: qps.course_id } as LoginOptionsAndAnalytics;
  }
  return undefined;
}

/**
 * Login a User with the LMS
 * @param userName Username/Email
 * @param password Password
 */
export default async function loginUser(
  userName: string,
  password: string,
): Promise<LoginResponse> {
  const qps = getAuthQueryParams();
  const analyticsAndOptions = analyticsAndOptionsForLogin(qps);
  const headers = (await requestAndBindCsrfToHeaders({
    accept: API_ACCEPT,
    "cache-control": "no-cache",
    pragma: "no-cache",
  })) as Record<string, string>;

  try {
    const loginRes = await fetch(buildApiEndpoint(AuthApiPath.UserLogin), {
      headers: {
        ...headers,
        "Content-Type": CONTENT_TYPE_FORM,
      } as Record<string, string>,
      body: analyticsAndOptions
        ? QueryString.stringify({
            email_or_username: userName,
            password,
            ...qps,
            analytics: JSON.stringify(analyticsAndOptions),
          })
        : QueryString.stringify({
            email_or_username: userName,
            password,
            ...qps,
          }),
      credentials: "include",
      method: "POST",
    });

    if (!loginRes.ok) {
      let errorMessage = "Login failed. Please try again.";
      let errorCode: string | undefined;
      let raw: any;
      try {
        const errorData = await loginRes.json();
        raw = errorData;
        errorMessage =
          errorData.value ||
          errorData.detail ||
          errorData.message ||
          errorData.error ||
          errorMessage;
        errorCode = errorData.error_code || errorData.code || errorData.error;
      } catch {
        // ignore JSON parse errors, use fallback message
      }

      // New Relic logging for HTTP errors
      newrelic.noticeError(new Error(`Login API failed: ${errorMessage}`), {
        submodule: "auth",
        endpoint: "UserLogin",
        status: loginRes.status,
        errorMessage,
        errorCode,
        errorType: "httpError",
        timestamp: new Date().toISOString(),
      });

      return {
        success: false,
        errorMessage,
        errorCode,
        status: loginRes.status,
        raw,
      };
    }
    return {
      data: await loginRes.json(),
      success: true,
    };
  } catch (e) {
    // New Relic logging for network errors
    newrelic.noticeError(e as Error, {
      submodule: "auth",
      endpoint: "UserLogin",
      context: "networkError",
      hasAnalytics: !!analyticsAndOptions,
      errorType: "networkError",
      timestamp: new Date().toISOString(),
    });

    return {
      success: false,
      errorMessage:
        "A network or server error occurred. Please try again later.",
      status: 0,
      raw: e,
    };
  }
}
